import { useState, useCallback, useRef } from 'react';
import { getColumnWidth, setColumnWidth } from '../../helpers/cookieUtils';

const COOKIE_KEY = 'cashflow_column_widths';
const DEFAULT_WIDTHS = {
  'Description':      150,
  'For. Allocation':  90,
  'For. Hours':       90,
  'Act. Hours':       90,
  'For. HV':       90,
  'For. h. Cost':     90,
  'For. h. Bill':     90,
  'For. Total Cost':  90,
  'For. Total Bill':  90,
  'For. CV':          90,
  'INV':              90,
  'AC':               90,
  'GP':               50,
};

export const useColumnWidths = () => {
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = {};
    Object.keys(DEFAULT_WIDTHS).forEach(key => {
      const stored = getColumnWidth(COOKIE_KEY, key);
      if (stored !== null) saved[key] = stored;
    });
    return { ...DEFAULT_WIDTHS, ...saved };
  });

  // Always-current ref — avoids stale closure in startResize
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;

  const startResize = useCallback((columnName, e) => {
    e.preventDefault();
    // Read from ref so we always get the latest width at drag start
    const startX = e.clientX;
    const startWidth = columnWidthsRef.current[columnName] ?? DEFAULT_WIDTHS[columnName] ?? 80;

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(50, startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [columnName]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setColumnWidth(COOKIE_KEY, columnName, columnWidthsRef.current[columnName]);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []); // no dependencies needed — reads from ref

  // getWidth reads from state — components calling this re-render when columnWidths changes
  const getWidth = useCallback((columnName) => {
    return columnWidths[columnName] ?? DEFAULT_WIDTHS[columnName] ?? 80;
  }, [columnWidths]);

  const resetWidths = useCallback(() => {
    setColumnWidths(DEFAULT_WIDTHS);
    Object.entries(DEFAULT_WIDTHS).forEach(([key, val]) => setColumnWidth(COOKIE_KEY, key, val));
  }, []);

  return { columnWidths, startResize, getWidth, resetWidths };
};
